import { Component, Input, Output } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';

export interface ConfirmDialogOption {
  value: string;
  label: string;
  description?: string;
}

@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.scss',
})
export class ConfirmDialogComponent {
  @Input() title: string = 'dialogs.confirmDialog.default.title';
  @Input() message: string = 'dialogs.confirmDialog.default.message';
  @Input() confirmLabel: string = 'buttons.confirm';
  @Input() cancelLabel: string = 'buttons.cancel';
  /** The confirming button is the primary (blue) action — always, also for
   *  delete/disable. The warning belongs in the message; a second red button
   *  is indistinguishable from Abbrechen. See design.md §"Dialog-Footer". */
  @Input() confirmStatus: string = 'primary';
  @Input() cancelStatus: string = 'danger';
  /** Optional radio choice rendered between message and footer. When set,
   *  confirming closes the dialog with the selected option's value instead
   *  of `true` (labels/descriptions are i18n keys). */
  @Input() options?: ConfirmDialogOption[];
  @Input() selectedOption?: string;
  /** Optional second paragraph (i18n key) — for a consequence the operator
   *  should read before confirming, e.g. what an action costs. */
  @Input() details?: string;
  /** Optional single text field between message and footer. Set `inputLabel`
   *  to switch it on. Exists so a short "type one value" question uses the
   *  portal's own dialog instead of the browser's `window.prompt`, which
   *  ignores the design system and names the host in its title bar.
   *
   *  Closing contract: with the field active the dialog closes with
   *  `{ confirmed: true, value }` rather than `true`, because an empty string
   *  is a legitimate answer (it clears the value) and would read as falsy at
   *  the call site. Callers without `inputLabel` keep the historic contract. */
  @Input() inputLabel?: string;
  @Input() inputValue: string = '';
  @Input() inputPlaceholder: string = '';
  @Input() inputMaxLength: number = 200;
  /** Show a "don't show this again" checkbox. The caller decides what to do
   *  with the answer; the dialog only reports it. */
  @Input() showSuppress: boolean = false;
  @Input() suppressLabel: string = 'dialogs.confirmDialog.suppress';
  protected suppress: boolean = false;

  @Output() onConfirm?: () => void;

  protected loading: boolean = false;

  constructor(protected dialogRef: NbDialogRef<ConfirmDialogComponent>) {}

  protected onDialogConfirm() {
    if (this.onConfirm) {
      this.loading = true;
      this.onConfirm();
    } else {
      // With the suppress checkbox the caller needs BOTH answers, so the
      // result becomes an object. Without it the historic `true` /
      // selected-option contract is untouched — dozens of call sites rely
      // on a plain truthy value.
      if (this.inputLabel) {
        this.dialogRef.close({
          confirmed: true,
          value: (this.inputValue || '').trim(),
        });
        return;
      }
      if (this.showSuppress) {
        this.dialogRef.close({
          confirmed: true,
          suppress: this.suppress,
          option: this.options?.length ? this.selectedOption : undefined,
        });
        return;
      }
      this.dialogRef.close(this.options?.length ? this.selectedOption : true);
    }
  }
}
